import React from 'react';
import { Button, Link, Box, Typography } from '@mui/material';
import { Launch as LaunchIcon, Info as InfoIcon } from '@mui/icons-material';

interface LocationInfoLinkProps {
  websiteUrl?: string;
  variant?: 'button' | 'link' | 'compact';
  size?: 'small' | 'medium' | 'large';
  color?: 'primary' | 'secondary' | 'inherit';
  showIcon?: boolean;
  className?: string;
}

const LocationInfoLink: React.FC<LocationInfoLinkProps> = ({
  websiteUrl,
  variant = 'button',
  size = 'small',
  color = 'primary',
  showIcon = true,
  className
}) => {
  // Don't render anything if no website URL is available
  if (!websiteUrl || websiteUrl.trim() === '') {
    return null;
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering parent click handlers
    window.open(websiteUrl, '_blank', 'noopener,noreferrer');
  };

  if (variant === 'link') {
    return (
      <Link
        component="button"
        variant="body2"
        onClick={handleClick}
        color={color}
        className={className}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          textDecoration: 'none',
          cursor: 'pointer',
          '&:hover': {
            textDecoration: 'underline'
          }
        }}
      >
        {showIcon && <InfoIcon fontSize="small" />}
        More Information
        <LaunchIcon fontSize="inherit" sx={{ ml: 0.25 }} />
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Box
        component="span"
        onClick={handleClick}
        className={className}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          color: `${color}.main`,
          cursor: 'pointer',
          fontSize: '0.875rem',
          '&:hover': {
            textDecoration: 'underline'
          }
        }}
      >
        {showIcon && <InfoIcon fontSize="small" />}
        <Typography variant="caption" color="inherit">
          More Info
        </Typography>
        <LaunchIcon fontSize="small" />
      </Box>
    );
  }

  // Default button variant
  return (
    <Button
      variant="outlined"
      size={size}
      color={color}
      onClick={handleClick}
      className={className}
      startIcon={showIcon ? <InfoIcon /> : undefined}
      endIcon={<LaunchIcon />}
      sx={{
        textTransform: 'none',
        fontSize: size === 'small' ? '0.75rem' : undefined
      }}
    >
      More Information
    </Button>
  );
};

export default LocationInfoLink;